import React, { PureComponent } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Card from '@components/Card';
import { isNullOrUndefined } from '@utils/object';
import SimpleButton from '@components/SimpleButton';
import Shell from '@shared/containers/Shell';
import FixedHeader from '@components/FixedHeader';
import ShellFooter from '@components/ShellFooter';
import OptionRowCounter from '../OptionRowCounter';
import OptionListSubtitle from '../OptionListSubtitle';

const MultipleOptionListContainer = styled.div`
  padding-top: 2vh;
  flex-grow: 1;

  .option_count_text {
    font-size: 14px;
    color: ${props => props.theme.colors.textPrimary};
    margin-bottom: 10px;

    .option_count_text_info {
      margin-left: 10px;
    }

    .completed {
      color: ${props => props.theme.colors.corporateGreen};
    }

    .incompleted {
      color: ${props => props.theme.colors.error};
    }
  }
`;

class MultipleOptionsList extends PureComponent {
  constructor(props) {
    super(props);

    const { initialValue } = props;

    const quantities = initialValue
      ? initialValue.options.reduce((acc, current) => {
          const { id } = current;
          if (id in acc) acc[id] += 1;
          else acc[id] = 1;
          return acc;
        }, {})
      : {};

    const selectionIsCompleted = this.isSelectionCompleted(quantities);

    this.state = { quantities, selectionIsCompleted };
  }

  isSelectionCompleted = quantities => {
    const { optionGroup } = this.props;
    const newTotalQuantity = this.getTotalQuantity(quantities);
    let selectionIsCompleted = newTotalQuantity === optionGroup.quantity;
    if (!isNullOrUndefined(optionGroup.minQuantity))
      selectionIsCompleted =
        newTotalQuantity >= optionGroup.minQuantity &&
        newTotalQuantity <= optionGroup.maxQuantity;

    return selectionIsCompleted;
  };

  getTotalQuantity = quantities =>
    Object.values(quantities).reduce((a, b) => a + b, 0);

  onSelect = id => quantity => {
    const { optionGroup } = this.props;

    this.setState(state => {
      const newQuantities = { ...state.quantities, [id]: quantity };
      const newTotalQuantity = this.getTotalQuantity(newQuantities);
      const selectionIsCompleted = this.isSelectionCompleted(newQuantities);
      const option = optionGroup.options.find(x => x.id === id);

      if (newTotalQuantity > optionGroup.quantity) {
        return state;
      }

      if (
        typeof option.maxQuantity === 'number' &&
        quantity > option.maxQuantity
      ) {
        return state;
      }

      return {
        quantities: newQuantities,
        selectionIsCompleted,
      };
    });
  };

  onConfirm = () => {
    const { optionGroup, onConfirm } = this.props;
    const { quantities, selectionIsCompleted } = this.state;

    if (selectionIsCompleted) {
      const options = Object.keys(quantities).reduce((acc, key) => {
        const value = quantities[key];
        const array = [...acc];
        for (let i = 1; i <= value; i += 1) array.push({ id: Number(key) });
        return array;
      }, []);

      onConfirm({
        id: optionGroup.id,
        options,
      });
    }
  };

  render() {
    const { optionGroup, onClose, Footer = SimpleButton } = this.props;
    const { quantities, selectionIsCompleted } = this.state;
    const totalQuantity = this.getTotalQuantity(quantities);

    const needsToAdd = optionGroup.quantity - totalQuantity;

    return (
      <Shell
        isModal
        header={<FixedHeader label={optionGroup.name} onClose={onClose} />}
        footer={
          <ShellFooter>
            <Footer
              elementId="product__option__list"
              label="Aceptar"
              color="primary"
              onClick={this.onConfirm}
              disabled={!selectionIsCompleted}
              block
            />
          </ShellFooter>
        }
      >
        <MultipleOptionListContainer>
          <OptionListSubtitle
            minQuantity={optionGroup.minQuantity}
            quantity={optionGroup.maxQuantity || optionGroup.quantity}
            needsToAdd={needsToAdd}
          />
          <Card direction="column">
            {optionGroup.options.map(x => (
              <OptionRowCounter
                key={x.id}
                option={x}
                quantity={quantities[x.id] || 0}
                amount={x.amount}
                modifiesPrice={x.modifiesPrice}
                onChangeQuantity={this.onSelect(x.id)}
              />
            ))}
          </Card>
        </MultipleOptionListContainer>
      </Shell>
    );
  }
}

MultipleOptionsList.propTypes = {
  optionGroup: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    options: PropTypes.array,
    quantity: PropTypes.number,
    minQuantity: PropTypes.number,
    maxQuantity: PropTypes.number,
  }).isRequired,
  initialValue: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  // Joker
  Footer: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

export { MultipleOptionsList };
export default MultipleOptionsList;
